import ReportDistributionIcon from "@uui/Mail01";
import MeetingReportAccessIcon from "@uui/Users03";
import JoinPreferencesIcon from "@uui/VideoRecorder";
import { useNavigate } from "react-router-dom";

import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";

import useConnectedProviders from "@app/analytics/hooks/sso/useConnectedProviders";
import usePopups from "@app/analytics/hooks/usePopups";
import useAuth from "@app/common/auth/useAuth";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import { withoutQueryParams } from "@app/common/utils";
import * as UserUtils from "@app/common/utils/userUtils";

import Button from "@rui/Button";
import IconBackground from "@rui/IconBackground";
import { Column, Row } from "@rui/Stack";
import { Img } from "@rui/styles";

import JoinPreferenceSettings from "./JoinPreferenceSettings";
import MeetingReportAccessSettings from "./MeetingReportAccessSettings";
import ReportDistributionSettings from "./ReportDistributionSettings";
import {
  CategoryLabel,
  ContentContainer,
  Footer,
  SettingSection,
} from "./styles";
import GradientHeader from "../assets/GradientHeader.png";
import { FeaturePopupContentTitle, FeaturePopupCard } from "../styles";

const SETTING_CATEGORIES = [
  {
    category: {
      Icon: JoinPreferencesIcon,
      label: "Join Preferences",
    },
    Component: JoinPreferenceSettings,
  },
  {
    category: {
      Icon: MeetingReportAccessIcon,
      label: "Meeting Report Access",
    },
    Component: MeetingReportAccessSettings,
  },
  {
    category: {
      Icon: ReportDistributionIcon,
      label: "Report Distribution",
    },
    Component: ReportDistributionSettings,
  },
];

const ConfirmSettings = ({
  firstName,
  onClose,
  priority,
}: {
  firstName: string;
  onClose: () => void;
  priority: number;
}) => {
  const { show, reset } = usePopups({
    id: "settings",
    priority,
    shouldShow: true,
    force: true,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={show} disableAutoFocus>
      <FeaturePopupCard
        sx={{
          width: { xxs: "600px", sm: "600px" },
          height: { xxs: "auto", sm: "auto" },
        }}
      >
        <Img src={GradientHeader} width="100%" height="16px" />
        <ContentContainer spacing={2} flex={1}>
          <Column spacing={0.5}>
            <FeaturePopupContentTitle>
              Review your settings
            </FeaturePopupContentTitle>
            <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
              Hey {firstName}, please review and confirm your settings for Read
              Assistant.
            </Typography>
          </Column>
          <Column>
            {SETTING_CATEGORIES.map(({ category, Component }) => (
              <SettingSection key={category.label} spacing={1.5}>
                <Row spacing={1} variant="left">
                  <IconBackground height={16} bgcolor="shades.ghost.500">
                    <category.Icon sx={{ color: "shades.primary.500" }} />
                  </IconBackground>
                  <CategoryLabel>{category.label}</CategoryLabel>
                </Row>
                <Component />
              </SettingSection>
            ))}
          </Column>
        </ContentContainer>
        <Footer variant="right">
          <Button onClick={handleClose}>Confirm Settings</Button>
        </Footer>
      </FeaturePopupCard>
    </Modal>
  );
};

const ConfirmSettingsGate = (props: { priority: number }) => {
  const navigate = useNavigate();

  const { user } = useAuth();
  const { hasConnectedCalendarProviders } = useConnectedProviders();
  const { updateUserMetadata } = useUserMetadata();

  if (
    !user ||
    !UserUtils.requireSettingsConfirmation(user) ||
    !hasConnectedCalendarProviders
  ) {
    // only show to logged-in users who have a calendar connected and are
    // required to confirm the settings before proceeding
    return null;
  }

  const handleClose = () => {
    navigate(
      withoutQueryParams(location.pathname, location.search, ["popup_id"])
    );
    updateUserMetadata.mutate({
      initialSettingsConfirmationState: "confirmed",
    });
  };

  return (
    <ConfirmSettings
      {...props}
      firstName={user.firstName}
      onClose={handleClose}
    />
  );
};

export default ConfirmSettingsGate;
